import type { GSplatResourceLease } from '@/plugins/gsplat/GSplatHandleBinding'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import {
  GSplatHandler,
  getGSplatUrlPath,
  type IGSplatParser,
} from '@/plugins/gsplat/loaders/GSplatHandler'
import { GSplatOctreeParser } from '@/plugins/gsplat/loaders/GSplatOctreeParser'
import { PlyParser, type PlyParserOptions } from '@/plugins/gsplat/loaders/PlyParser'
import { SogBundleParser } from '@/plugins/gsplat/loaders/SogBundleParser'
import { SogParser, type SogParserOptions } from '@/plugins/gsplat/loaders/SogParser'
import { SplatParser, type SplatParserOptions } from '@/plugins/gsplat/loaders/SplatParser'
import type { GaussianSplatLoadRequest } from '@/plugins/gsplat/types/PluginTypes'
import type { GSplatOctreeFileResource } from '@/plugins/gsplat/unified/octree/GSplatOctree'
import type { AssetLoader } from '@/shared/types/assets'
import type { Disposable } from '@/shared/types/core'

interface CanonicalGSplatResourceOwner extends GSplatOctreeFileResource {
  readonly numSplats: number
  acquire(): GSplatResourceLease
}

type CanonicalParserOptions = PlyParserOptions & SplatParserOptions & SogParserOptions

interface CanonicalLoaderDefinition {
  readonly format: string
  readonly detectPriority: number
  readonly pattern: RegExp
}

const CANONICAL_LOADERS: readonly CanonicalLoaderDefinition[] = Object.freeze([
  { format: 'lod-meta', detectPriority: 10, pattern: /(?:^|[/\\])lod-meta\.json$/i },
  { format: 'sog-json', detectPriority: 6, pattern: /(?:^|[/\\])meta\.json$/i },
  { format: 'sog', detectPriority: 5, pattern: /\.sog$/i },
  { format: 'splat', detectPriority: 3, pattern: /\.splat$/i },
  { format: 'ply', detectPriority: 0, pattern: /\.ply$/i },
])

export interface CanonicalGSplatLoaderRegistryOptions {
  readonly adapter: GSplatGraphicsAdapter
  readonly withCredentials: boolean
  readonly readMaxSplats: () => number
  readonly readLoadSHN: () => boolean
  readonly readMaxConcurrentLoads: () => number
  /** CPU sorting/picking 需要 centers；GPU-only 管线可避免这份 CPU 展开。 */
  readonly readPrepareCenters?: () => boolean
}

export interface CanonicalGSplatLoaderRegistry extends Disposable {
  readonly loaders: readonly AssetLoader<GaussianSplatLoadRequest, GSplatResourceLease>[]
  readonly formats: readonly string[]
}

/**
 * PLY/compressed/SOG/bundle/LOD 共用的唯一 parser registry。
 *
 * Octree 文件递归回到同一个 handler，因此不会再进入 WebGL-only SOG/LOD manager loader。
 */
export function createCanonicalGSplatLoaderRegistry(
  options: CanonicalGSplatLoaderRegistryOptions
): CanonicalGSplatLoaderRegistry {
  const handler = new GSplatHandler<CanonicalGSplatResourceOwner, CanonicalParserOptions>()
  const add = (parser: unknown): void => {
    handler.addParser(parser as IGSplatParser<CanonicalGSplatResourceOwner, CanonicalParserOptions>)
  }

  // Handler newest-first；Octree 必须晚于 broad JSON SOG parser 注册。
  add(new PlyParser())
  add(new SplatParser())
  add(new SogBundleParser())
  add(new SogParser())
  add(
    new GSplatOctreeParser<CanonicalGSplatResourceOwner, CanonicalParserOptions>({
      maxConcurrentLoads: options.readMaxConcurrentLoads(),
    })
  )

  const loaders = CANONICAL_LOADERS.map((definition) => ({
    format: definition.format,
    detectPriority: definition.detectPriority,
    canLoad: ({ url }: GaussianSplatLoadRequest) => definition.pattern.test(getGSplatUrlPath(url)),
    load: async (request: GaussianSplatLoadRequest, context) => {
      const signal = context.signal ?? request.signal
      const parserOptions = createParserOptions(options, request, context.onProgress)
      let owner: CanonicalGSplatResourceOwner | null = null
      try {
        owner = await handler.load(
          request.source ? { load: request.source, original: request.url } : request.url,
          {
            ...(signal ? { signal } : {}),
            assetFileUrl: request.url,
            parserOptions,
          }
        )
        throwIfAborted(signal)
        const lease = owner.acquire()
        owner.dispose()
        owner = null
        context.onProgress?.(1)
        return lease
      } catch (error) {
        owner?.dispose()
        throw error
      }
    },
  }))

  let disposed = false
  return Object.freeze({
    loaders: Object.freeze(loaders),
    formats: Object.freeze(CANONICAL_LOADERS.map(({ format }) => format)),
    dispose: () => {
      if (disposed) return
      disposed = true
      handler.dispose()
    },
  })
}

function createParserOptions(
  registry: CanonicalGSplatLoaderRegistryOptions,
  request: GaussianSplatLoadRequest,
  onProgress?: (progress: number) => void
): CanonicalParserOptions {
  const raw: Readonly<Record<string, unknown>> = {
    ...(request.options ?? {}),
    ...(request.loaderOptions ?? {}),
  }
  const progress = (loaded: number, total: number): void => {
    onProgress?.(total > 0 ? Math.min(1, loaded / total) : 0)
  }
  return {
    adapter: registry.adapter,
    withCredentials: readBoolean(raw.withCredentials, registry.withCredentials),
    mapUrl: readMapUrl(raw.mapUrl),
    loadSHN: readBoolean(raw.loadSHN, registry.readLoadSHN()),
    reorder: readOptionalBoolean(raw.reorder),
    decompress: readOptionalBoolean(raw.decompress),
    prepareCenters:
      readOptionalBoolean(raw.prepareCenters) ??
      registry.readPrepareCenters?.() ??
      registry.adapter.backend === 'webgl2',
    progress,
    validateSplatCount: (count) => validateSplatCount(count, registry.readMaxSplats()),
  }
}

function validateSplatCount(count: number, limit: number): void {
  if (!Number.isSafeInteger(count) || count <= 0) {
    throw new Error(`GSplatSystem: 非法 splatCount，actual=${count}`)
  }
  if (!Number.isSafeInteger(limit) || limit < 0) {
    throw new Error(`GSplatSystem: 非法 maxSplats，limit=${limit}（0 表示不限制）`)
  }
  // 0 = 不限制：解析阶段不做准入拒绝，实际显存占用由设备限制在分配时兜底。
  if (limit > 0 && count > limit) {
    throw new Error(`GSplatSystem: splatCount 超出 maxSplats，actual=${count}, limit=${limit}`)
  }
}

function readOptionalBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined
}

function readBoolean(value: unknown, fallback: boolean): boolean {
  return readOptionalBoolean(value) ?? fallback
}

function readMapUrl(value: unknown): ((filename: string) => string) | undefined {
  return typeof value === 'function' ? (value as (filename: string) => string) : undefined
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('GSplat canonical load 已取消', 'AbortError')
}
